import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getClients, createClient, updateClient, deleteClient, bulkDeleteClients, bulkUpdateClients, getClientBeneficiaries, addClientBeneficiary, deleteBeneficiary, getImportTemplateUrl, importClientsExcel, setClientPortalAccess, disableClientPortalAccess, assignBeneficiaryFromClient, listClientDocuments, uploadClientDocument, deleteClientDocument, listBeneficiaryDocuments, uploadBeneficiaryDocument, deleteBeneficiaryDocument, getFileUrl } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, MagnifyingGlass, Pencil, Trash, Eye, IdentificationCard, UserPlus, ArrowsLeftRight, Buildings, FileText, Paperclip, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ClientsPage = () => {
  const { t } = useTranslation();
  const { user: authUser } = useAuth();
  // Dealer management features (filter, bulk-assign, type=dealer creation,
  // portal logins, beneficiaries-as-dealer-feature) are restricted to the
  // master_admin (developer/system owner). Regular tenant admins keep all
  // existing dealer-flagged clients in the list but no longer get any dealer
  // management affordances.
  const isMaster = authUser?.role === 'master_admin';
  const [clients, setClients] = useState([]);
  // Independent caches used by the dropdowns so they don't get "locked" to
  // whatever the table filter is currently showing.
  const [allDealers, setAllDealers] = useState([]);
  const [allIndividuals, setAllIndividuals] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('type'); // 'type' | 'dealer'
  const [bulkTargetType, setBulkTargetType] = useState('individual');
  const [bulkTargetDealer, setBulkTargetDealer] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'individual' | 'dealer'
  // dealerFilter: 'any' | 'unassigned' | 'assigned' | <dealer user_id>
  const [dealerFilter, setDealerFilter] = useState('any');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [beneficiaryDialogOpen, setBeneficiaryDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [newBeneficiary, setNewBeneficiary] = useState({
    name: '', id_type: 'national_id', id_number: '',
    nin_number: '', passport_number: '', id_card_number: '',
    phone: '', email: '', address: '', date_of_birth: '',
    relationship: 'customer', notes: '',
  });
  // 'pick' = assign existing individual client | 'new' = create fresh end-client
  const [beneficiaryMode, setBeneficiaryMode] = useState('pick');
  const [pickClientId, setPickClientId] = useState('');
  const [pickClientSearch, setPickClientSearch] = useState('');
  // Documents dialog state: target = { kind: 'client'|'beneficiary', client_id, ben_id, name }
  const [docsDialogTarget, setDocsDialogTarget] = useState(null);
  const [docsList, setDocsList] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsUploading, setDocsUploading] = useState(false);
  const [formData, setFormData] = useState({
    surname: '',
    given_name: '',
    email: '',
    phone: '',
    client_type: 'individual',
    company_name: '',
    address: '',
    nin_number: '',
    passport_number: '',
    id_card_number: '',
    date_of_birth: '',
    notes: '',
    portal_password: '',
    master_dealer_id: '',
  });

  const loadClients = useCallback(async () => {
    try {
      const params = { search, limit: 10000 };
      if (typeFilter !== 'all') params.client_type = typeFilter;
      const res = await getClients(params);
      setClients(res.data.clients);
      setSelectedIds([]); // clear selection on reload
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter]);

  useEffect(() => { loadClients(); }, [loadClients]);

  // Load full dealer + individual rosters once for the dropdowns (independent
  // of the table filter so they don't get "locked")
  const loadDirectories = useCallback(async () => {
    try {
      const [d, i] = await Promise.all([
        getClients({ client_type: 'dealer', limit: 10000 }),
        getClients({ client_type: 'individual', limit: 10000 }),
      ]);
      setAllDealers(d.data.clients || []);
      setAllIndividuals(i.data.clients || []);
    } catch {
      // silently ignore — table data is the source of truth either way
    }
  }, []);
  useEffect(() => { loadDirectories(); }, [loadDirectories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedClient) {
        await updateClient(selectedClient.user_id, formData);
        toast.success('Client updated');
      } else {
        await createClient(formData);
        toast.success('Client created');
      }
      setDialogOpen(false);
      resetForm();
      loadClients();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend may return a structured 409 with existing_client + matched_on
      if (err.response?.status === 409 && detail && typeof detail === 'object') {
        const ex = detail.existing_client || {};
        const matched = (detail.matched_on || []).join(', ');
        toast.error(
          `Duplicate client — already exists (matched on: ${matched || 'identifier'})`,
          {
            description: `${ex.name || ''}${ex.phone ? ' · ' + ex.phone : ''}${ex.nin_number ? ' · NIN ' + ex.nin_number : ''}`,
            duration: 8000,
          }
        );
      } else {
        toast.error((typeof detail === 'string' && detail) || 'Operation failed');
      }
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm('Are you sure you want to delete this dealer?')) return;
    try {
      await deleteClient(client.user_id);
      toast.success('Client deleted');
      loadClients();
    } catch (err) {
      toast.error('Failed to delete client');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === clients.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(clients.map((c) => c.user_id));
    }
  };
  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected client${selectedIds.length === 1 ? '' : 's'}? Clients with existing orders will be skipped.`)) return;
    setBulkDeleting(true);
    try {
      const res = await bulkDeleteClients(selectedIds);
      const { deleted_count, blocked } = res.data;
      if (deleted_count > 0) toast.success(`${deleted_count} client${deleted_count === 1 ? '' : 's'} deleted`);
      if (blocked?.length) {
        toast.warning(`${blocked.length} skipped (have orders)`, {
          description: blocked.slice(0, 5).map((b) => `${b.name} · ${b.order_count} order${b.order_count === 1 ? '' : 's'}`).join('\n'),
          duration: 8000,
        });
      }
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const submitBulkUpdate = async () => {
    const payload = { client_ids: selectedIds };
    if (bulkAction === 'type') {
      payload.client_type = bulkTargetType;
    } else if (bulkAction === 'dealer') {
      // empty string ↔ unassign
      payload.master_dealer_id = bulkTargetDealer || '';
    }
    setBulkUpdating(true);
    try {
      const r = await bulkUpdateClients(payload);
      const data = r.data || {};
      toast.success(
        bulkAction === 'type'
          ? `Updated ${data.updated_count} client(s) to ${bulkTargetType}`
          : (bulkTargetDealer
              ? `Assigned ${data.updated_count} client(s) to dealer`
              : `Unassigned ${data.updated_count} client(s) from any dealer`)
      );
      if ((data.skipped || []).length) {
        toast.warning(`${data.skipped.length} skipped`);
      }
      setBulkDialogOpen(false);
      setSelectedIds([]);
      loadClients();
      loadDirectories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkUpdating(false);
    }
  };

  const openEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      surname: client.surname || '',
      given_name: client.given_name || '',
      email: client.email || '',
      phone: client.phone || '',
      client_type: client.client_type || 'individual',
      company_name: client.company_name || '',
      address: client.address || '',
      nin_number: client.nin_number || '',
      passport_number: client.passport_number || '',
      id_card_number: client.id_card_number || '',
      date_of_birth: client.date_of_birth || '',
      notes: client.notes || ''
    });
    setDialogOpen(true);
  };

  const openView = async (client) => {
    setSelectedClient(client);
    setViewDialogOpen(true);
    // Load beneficiaries
    try {
      const res = await getClientBeneficiaries(client.user_id);
      setBeneficiaries(res.data || []);
    } catch {
    }
  };

  const openBeneficiaryDialog = (client) => {
    setSelectedClient(client);
    setBeneficiaryMode(client.client_type === 'dealer' ? 'pick' : 'new');
    setNewBeneficiary({
      name: '', id_type: 'national_id', id_number: '',
      nin_number: '', passport_number: '', id_card_number: '',
      phone: '', email: '', address: '', date_of_birth: '',
      relationship: 'customer', notes: '',
    });
    setPickClientId('');
    setPickClientSearch('');
    setBeneficiaryDialogOpen(true);
  };

  const handleAddBeneficiary = async (e) => {
    e.preventDefault();
    try {
      if (beneficiaryMode === 'pick') {
        if (!pickClientId) { toast.error('Please pick an individual client to assign'); return; }
        await assignBeneficiaryFromClient(selectedClient.user_id, pickClientId);
        toast.success('Client assigned to dealer — all future orders for this individual will go under this dealer.');
      } else {
        if (!newBeneficiary.name.trim()) { toast.error('Full name is required'); return; }
        // Sync id_number with the dominant ID for legacy compatibility
        const idNumber = newBeneficiary.nin_number
          || newBeneficiary.passport_number
          || newBeneficiary.id_card_number
          || newBeneficiary.id_number;
        await addClientBeneficiary(selectedClient.user_id, { ...newBeneficiary, id_number: idNumber });
        toast.success('End client added');
      }
      setBeneficiaryDialogOpen(false);
      if (viewDialogOpen) {
        const res = await getClientBeneficiaries(selectedClient.user_id);
        setBeneficiaries(res.data || []);
      }
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  const handleDeleteBeneficiary = async (benId) => {
    if (!window.confirm('Delete this ID holder?')) return;
    try {
      await deleteBeneficiary(benId);
      toast.success('End client deleted');
      const res = await getClientBeneficiaries(selectedClient.user_id);
      setBeneficiaries(res.data || []);
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Documents (client master or beneficiary end-client)
  const openDocsDialog = async (target) => {
    setDocsDialogTarget(target);
    setDocsList([]);
    setDocsLoading(true);
    try {
      const res = target.kind === 'client'
        ? await listClientDocuments(target.client_id)
        : await listBeneficiaryDocuments(target.client_id, target.ben_id);
      setDocsList(res.data || []);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setDocsLoading(false);
    }
  };

  const refreshDocs = async () => {
    if (!docsDialogTarget) return;
    try {
      const res = docsDialogTarget.kind === 'client'
        ? await listClientDocuments(docsDialogTarget.client_id)
        : await listBeneficiaryDocuments(docsDialogTarget.client_id, docsDialogTarget.ben_id);
      setDocsList(res.data || []);
    } catch {}
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docsDialogTarget) return;
    setDocsUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (docsDialogTarget.kind === 'client') {
        await uploadClientDocument(docsDialogTarget.client_id, fd);
      } else {
        await uploadBeneficiaryDocument(docsDialogTarget.client_id, docsDialogTarget.ben_id, fd);
      }
      toast.success('Document uploaded');
      await refreshDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setDocsUploading(false);
      e.target.value = '';
    }
  };

  const handleDocDelete = async (fileId) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      if (docsDialogTarget.kind === 'client') {
        await deleteClientDocument(docsDialogTarget.client_id, fileId);
      } else {
        await deleteBeneficiaryDocument(docsDialogTarget.client_id, docsDialogTarget.ben_id, fileId);
      }
      toast.success('Document removed');
      await refreshDocs();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const resetForm = () => {
    setSelectedClient(null);
    setFormData({
      surname: '',
      given_name: '',
      email: '',
      phone: '',
      client_type: 'individual',
      company_name: '',
      address: '',
      nin_number: '',
      passport_number: '',
      id_card_number: '',
      date_of_birth: '',
      notes: '',
      portal_password: '',
      master_dealer_id: '',
    });
  };

  return (
    <div data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('clients.title')}</h1>
          <p className="text-muted-foreground text-sm">
            {clients.length} client{clients.length === 1 ? '' : 's'}{search ? ' (filtered)' : ''} · individuals and dealers
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setImportResult(null); setImportDialogOpen(true); }} data-testid="import-clients-btn">
            Import Excel
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('clients.addClient')}
          </Button>
        </div>
      </div>

      {/* Search + Filter + Bulk actions */}
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-brutalist pl-10"
            data-testid="client-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-44" data-testid="client-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="individual">Individuals</SelectItem>
            <SelectItem value="dealer">Dealers</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dealerFilter} onValueChange={setDealerFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="client-dealer-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="any">Any dealer link</SelectItem>
            <SelectItem value="unassigned">Standalone (no dealer)</SelectItem>
            <SelectItem value="assigned">Under any dealer</SelectItem>
            {allDealers.map((d) => (
              <SelectItem key={d.user_id} value={d.user_id}>
                Under {d.name}{d.company_name ? ` (${d.company_name})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedIds.length > 0 && (
          <>
            <Button
              variant="outline"
              onClick={() => { setBulkAction('type'); setBulkDialogOpen(true); }}
              disabled={bulkDeleting || bulkUpdating}
              data-testid="bulk-change-type-btn"
            >
              <ArrowsLeftRight className="w-4 h-4 mr-2" />
              Change type ({selectedIds.length})
            </Button>
            <Button
              variant="outline"
              onClick={() => { setBulkAction('dealer'); setBulkTargetDealer(''); setBulkDialogOpen(true); }}
              disabled={bulkDeleting || bulkUpdating}
              data-testid="bulk-assign-dealer-btn"
            >
              <Buildings className="w-4 h-4 mr-2" />
              Assign to dealer ({selectedIds.length})
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-clients-btn"
            >
              <Trash className="w-4 h-4 mr-2" />
              {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.length} selected`}
            </Button>
          </>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {search ? 'No dealers found matching your search' : 'No dealers yet. Add your first dealer!'}
        </div>
      ) : (
        <div className="border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50">
                <th className="table-header w-10 px-3">
                  <input
                    type="checkbox"
                    checked={clients.length > 0 && selectedIds.length === clients.length}
                    onChange={toggleSelectAll}
                    className="cursor-pointer"
                    data-testid="select-all-clients"
                    aria-label="Select all"
                  />
                </th>
                <th className="table-header">{t('clients.name')}</th>
                <th className="table-header">Type</th>
                <th className="table-header">{t('clients.email')}</th>
                <th className="table-header hidden sm:table-cell">{t('clients.phone')}</th>
                <th className="table-header hidden md:table-cell">NIN / Passport</th>
                <th className="table-header text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {clients.filter((client) => {
                if (dealerFilter === 'any') return true;
                if (dealerFilter === 'unassigned') return !client.master_dealer_id;
                if (dealerFilter === 'assigned') return !!client.master_dealer_id;
                return client.master_dealer_id === dealerFilter;
              }).map((client, idx) => (
                <tr key={client.user_id} className={`animate-fade-in stagger-${(idx % 5) + 1} ${selectedIds.includes(client.user_id) ? 'bg-primary/5' : ''}`}>
                  <td className="px-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(client.user_id)}
                      onChange={() => toggleSelect(client.user_id)}
                      className="cursor-pointer"
                      data-testid={`select-client-${client.user_id}`}
                      aria-label={`Select ${client.name}`}
                    />
                  </td>
                  <td className="table-cell font-medium">
                    {client.name}
                    {client.company_name ? <span className="text-xs text-muted-foreground ml-1">({client.company_name})</span> : ''}
                    {client.client_type === 'dealer' && client.beneficiary_count > 0 && (
                      <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700" title="End clients">
                        {client.beneficiary_count} end client{client.beneficiary_count === 1 ? '' : 's'}
                      </span>
                    )}
                    {client.master_dealer_id && (
                      <div className="text-[10px] mt-1">
                        <span className="inline-block bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded-full" title="Orders for this individual go under the dealer below">
                          → under dealer <strong>{client.master_dealer_name || client.master_dealer_id.slice(0, 8)}</strong>
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="table-cell">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${client.client_type === 'dealer' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                      {client.client_type === 'dealer' ? 'Dealer' : 'Individual'}
                    </span>
                    {isMaster && client.has_portal_login && (
                      <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700" title="Has portal login">
                        Portal
                      </span>
                    )}
                  </td>
                  <td className="table-cell text-muted-foreground">{client.email}</td>
                  <td className="table-cell hidden sm:table-cell">{client.phone}</td>
                  <td className="table-cell hidden md:table-cell font-mono text-sm">{client.nin_number || client.passport_number || client.national_id || '-'}</td>
                  <td className="table-cell text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openView(client)} data-testid={`view-client-${client.user_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openBeneficiaryDialog(client)} title="Add end client" data-testid={`add-beneficiary-${client.user_id}`}>
                        <UserPlus className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(client)} data-testid={`edit-client-${client.user_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(client)} className="text-destructive" data-testid={`delete-client-${client.user_id}`}>
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedClient ? t('clients.editClient') : t('clients.addClient')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client Type + Company */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Type *</Label>
                <Select value={formData.client_type} onValueChange={(v) => setFormData({ ...formData, client_type: v })}>
                  <SelectTrigger data-testid="client-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="dealer">Dealer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.client_type === 'dealer' && (
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="input-brutalist"
                    data-testid="client-company-input"
                  />
                </div>
              )}
              {formData.client_type === 'individual' && !selectedClient && (
                <div className="space-y-2">
                  <Label>Assign to dealer <span className="text-xs text-muted-foreground">(optional)</span></Label>
                  <Select
                    value={formData.master_dealer_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, master_dealer_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="client-master-dealer-select">
                      <SelectValue placeholder="— None —" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="none">— None (standalone individual) —</SelectItem>
                      {allDealers.map((d) => (
                        <SelectItem key={d.user_id} value={d.user_id}>
                          {d.name}{d.company_name ? ` (${d.company_name})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.master_dealer_id && (
                    <p className="text-[11px] text-amber-700">
                      This individual will be linked as an end-client of the selected dealer.
                      Future orders for them will route through the dealer.
                    </p>
                  )}
                </div>
              )}
            </div>
            {/* Surname + Given Name */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Surname *</Label>
                <Input
                  value={formData.surname}
                  onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                  required
                  className="input-brutalist"
                  data-testid="client-surname-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Given Name *</Label>
                <Input
                  value={formData.given_name}
                  onChange={(e) => setFormData({ ...formData, given_name: e.target.value })}
                  required
                  className="input-brutalist"
                  data-testid="client-given-name-input"
                />
              </div>
            </div>
            {/* Email + Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('clients.email')} *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input-brutalist"
                  data-testid="client-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('clients.phone')} *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="input-brutalist"
                  data-testid="client-phone-input"
                />
              </div>
            </div>
            {/* NIN + Passport + ID Card — only for Individual clients */}
            {formData.client_type !== 'dealer' && (
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>NIN Number</Label>
                <Input
                  value={formData.nin_number}
                  onChange={(e) => setFormData({ ...formData, nin_number: e.target.value })}
                  placeholder="National ID Number"
                  className="input-brutalist font-mono"
                  data-testid="client-nin-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Passport Number</Label>
                <Input
                  value={formData.passport_number}
                  onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                  className="input-brutalist font-mono"
                  data-testid="client-passport-input"
                />
              </div>
              <div className="space-y-2">
                <Label>ID Card Number</Label>
                <Input
                  value={formData.id_card_number}
                  onChange={(e) => setFormData({ ...formData, id_card_number: e.target.value })}
                  placeholder="If no passport"
                  className="input-brutalist font-mono"
                  data-testid="client-id-card-input"
                />
              </div>
            </div>
            )}
            {/* Address (French) */}
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Adresse en français"
                className="input-brutalist"
                data-testid="client-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('clients.notes')}</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="input-brutalist"
              />
            </div>
            {isMaster && selectedClient && (
              <div className="space-y-2 p-3 border border-emerald-200 bg-emerald-50/30" data-testid="portal-access-card">
                <Label className="font-semibold text-base">Portal access</Label>
                {selectedClient.has_portal_login ? (
                  <>
                    <p className="text-xs text-emerald-700">✓ This client can log in at the same login page using their email.</p>
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                      <Input
                        type="text"
                        placeholder="New password (leave empty to keep current)"
                        value={formData.portal_password}
                        onChange={(e) => setFormData({ ...formData, portal_password: e.target.value })}
                        data-testid="portal-reset-password-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!formData.portal_password || formData.portal_password.length < 6}
                        onClick={async () => {
                          try {
                            await setClientPortalAccess(selectedClient.user_id, formData.portal_password);
                            toast.success('Password reset');
                            setFormData({ ...formData, portal_password: '' });
                            loadClients();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Reset failed');
                          }
                        }}
                        data-testid="portal-reset-btn"
                      >Reset password</Button>
                      <Button
                        type="button"
                        variant="ghost"
                        className="text-destructive"
                        onClick={async () => {
                          if (!window.confirm('Disable portal access for this client? They will no longer be able to log in.')) return;
                          try {
                            await disableClientPortalAccess(selectedClient.user_id);
                            toast.success('Portal access disabled');
                            setIsCreateOpen(false);
                            loadClients();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Disable failed');
                          }
                        }}
                        data-testid="portal-disable-btn"
                      >Disable</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">This client doesn't have a portal login yet. Set a password below to enable them to log in.</p>
                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                      <Input
                        type="text"
                        placeholder="Initial password (min 6 chars)"
                        value={formData.portal_password}
                        onChange={(e) => setFormData({ ...formData, portal_password: e.target.value })}
                        data-testid="portal-enable-password-input"
                      />
                      <Button
                        type="button"
                        disabled={!formData.portal_password || formData.portal_password.length < 6}
                        onClick={async () => {
                          try {
                            await setClientPortalAccess(selectedClient.user_id, formData.portal_password);
                            toast.success('Portal access enabled — share the password with the client.');
                            setFormData({ ...formData, portal_password: '' });
                            setIsCreateOpen(false);
                            loadClients();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Enable failed');
                          }
                        }}
                        data-testid="portal-enable-btn"
                      >Enable portal access</Button>
                    </div>
                  </>
                )}
              </div>
            )}
            {isMaster && !selectedClient && (
              <div className="space-y-2 p-3 border border-blue-200 bg-blue-50/30">
                <Label className="flex items-center gap-1">
                  Portal login password <span className="text-[10px] text-muted-foreground font-normal">(optional — leave empty to skip)</span>
                </Label>
                <Input
                  type="text"
                  value={formData.portal_password}
                  onChange={(e) => setFormData({ ...formData, portal_password: e.target.value })}
                  placeholder="Set a password to give this client portal access"
                  data-testid="portal-password-input"
                />
                <p className="text-[11px] text-muted-foreground">If set, the client can log in at the same login page using their email + this password to see their orders, request new orders and chat with you.</p>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" className="btn-primary" data-testid="client-submit-btn">
                {t('common.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Client Details</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${selectedClient.client_type === 'dealer' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                  {selectedClient.client_type === 'dealer' ? 'Dealer' : 'Individual'}
                </span>
                {selectedClient.company_name && <span className="text-sm text-muted-foreground">{selectedClient.company_name}</span>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="overline">Surname</p>
                  <p className="font-medium">{selectedClient.surname || selectedClient.name || '-'}</p>
                </div>
                <div>
                  <p className="overline">Given Name</p>
                  <p className="font-medium">{selectedClient.given_name || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="overline">{t('clients.email')}</p>
                  <p>{selectedClient.email}</p>
                </div>
                <div>
                  <p className="overline">{t('clients.phone')}</p>
                  <p>{selectedClient.phone}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="overline">NIN Number</p>
                  <p className="font-mono">{selectedClient.nin_number || selectedClient.national_id || '-'}</p>
                </div>
                <div>
                  <p className="overline">Passport</p>
                  <p className="font-mono">{selectedClient.passport_number || '-'}</p>
                </div>
                <div>
                  <p className="overline">ID Card</p>
                  <p className="font-mono">{selectedClient.id_card_number || '-'}</p>
                </div>
              </div>
              <div>
                <p className="overline">Adresse</p>
                <p>{selectedClient.address || '-'}</p>
              </div>
              <div>
                <p className="overline">{t('clients.notes')}</p>
                <p className="text-muted-foreground">{selectedClient.notes || '-'}</p>
              </div>

              {/* Client-level documents */}
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText weight="duotone" className="w-5 h-5" />
                    <p className="font-bold">Client Documents</p>
                  </div>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => openDocsDialog({ kind: 'client', client_id: selectedClient.user_id, name: selectedClient.name })}
                    data-testid="open-client-docs"
                  >
                    <Paperclip className="w-4 h-4 mr-1" />
                    Manage documents
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  ID scans, passport, address proof, etc. — attached to the client master record.
                </p>
              </div>

              {/* Saved ID Holders / Beneficiaries */}
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <IdentificationCard weight="duotone" className="w-5 h-5" />
                    <p className="font-bold">Saved End Clients</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openBeneficiaryDialog(selectedClient)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  End clients whose IDs can be used for orders
                </p>
                {beneficiaries.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-sm border border-dashed border-border">
                    No saved end clients. Dealer's own ID can always be used.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {beneficiaries.map((ben) => (
                      <div key={ben.id} className="flex items-center justify-between p-3 border border-border">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{ben.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {ben.id_type === 'passport' ? 'Passport' : 'National ID'}: <span className="font-mono">{ben.id_number}</span> • {ben.relationship}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => openDocsDialog({ kind: 'beneficiary', client_id: selectedClient.user_id, ben_id: ben.id, name: ben.name })}
                            title="Documents"
                            data-testid={`open-ben-docs-${ben.id}`}
                          >
                            <Paperclip className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteBeneficiary(ben.id)} className="text-destructive" title="Delete">
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Documents Dialog (client master OR beneficiary end-client) */}
      <Dialog open={!!docsDialogTarget} onOpenChange={(o) => !o && setDocsDialogTarget(null)}>
        <DialogContent className="max-w-2xl" data-testid="docs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText weight="duotone" />
              Documents — {docsDialogTarget?.name || ''}
              <span className="text-xs font-normal text-muted-foreground">
                ({docsDialogTarget?.kind === 'client' ? 'client master' : 'end client'})
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground text-xs">
              Upload ID scans, passport scans, address proofs, etc. Images (PNG/JPG/WEBP/HEIC) and PDFs up to 15 MB. You can add or remove documents anytime — even after the order is shipped.
            </p>
            <div className="flex items-center gap-3 border border-border rounded p-3 bg-muted/20">
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleDocUpload}
                disabled={docsUploading}
                data-testid="client-doc-upload-input"
              />
              {docsUploading && <span className="text-xs text-muted-foreground">Uploading…</span>}
            </div>

            {docsLoading ? (
              <p>Loading documents…</p>
            ) : docsList.length === 0 ? (
              <p className="text-muted-foreground italic">No documents yet.</p>
            ) : (
              <div className="space-y-2">
                {docsList.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border border-border rounded p-2 bg-white">
                    <div className="flex items-center gap-3 min-w-0">
                      {(d.content_type || '').startsWith('image/') ? (
                        <img src={getFileUrl(d.id)} alt={d.original_filename} className="w-10 h-10 object-cover border" />
                      ) : (
                        <FileText size={32} className="text-muted-foreground" />
                      )}
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.original_filename}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {((d.size || 0) / 1024).toFixed(1)} KB · {new Date(d.created_at).toLocaleString()}
                          {d.uploaded_by_name ? ` · by ${d.uploaded_by_name}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <a href={getFileUrl(d.id)} target="_blank" rel="noreferrer" className="p-2 text-muted-foreground hover:text-foreground" title="Open" data-testid={`open-doc-${d.id}`}>
                        <DownloadSimple size={16} />
                      </a>
                      <Button size="sm" variant="ghost" onClick={() => handleDocDelete(d.id)} title="Remove" data-testid={`delete-doc-${d.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocsDialogTarget(null)} data-testid="close-docs-dialog">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Beneficiary Dialog */}
      <Dialog open={beneficiaryDialogOpen} onOpenChange={setBeneficiaryDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="beneficiary-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedClient?.client_type === 'dealer' ? `Add end client to ${selectedClient?.name || 'dealer'}` : 'Add End Client'}
            </DialogTitle>
          </DialogHeader>
          {selectedClient?.client_type === 'dealer' && (
            <div className="flex gap-2 mb-3">
              <Button
                type="button" size="sm"
                variant={beneficiaryMode === 'pick' ? 'default' : 'outline'}
                onClick={() => setBeneficiaryMode('pick')}
                data-testid="beneficiary-mode-pick"
              >
                Pick existing individual client
              </Button>
              <Button
                type="button" size="sm"
                variant={beneficiaryMode === 'new' ? 'default' : 'outline'}
                onClick={() => setBeneficiaryMode('new')}
                data-testid="beneficiary-mode-new"
              >
                Create new end client
              </Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground mb-4">
            {beneficiaryMode === 'pick'
              ? 'Pick an existing individual from your client list. The dealer becomes their master account, and any future orders for this individual will go under this dealer.'
              : 'Create a fresh end-client record under this dealer. Their documents are stored on this end-client profile.'}
          </p>

          {beneficiaryMode === 'pick' ? (
            <form onSubmit={handleAddBeneficiary} className="space-y-4">
              <div>
                <Label>Search</Label>
                <Input
                  value={pickClientSearch}
                  onChange={(e) => setPickClientSearch(e.target.value)}
                  placeholder="Name, phone, NIN, passport…"
                  data-testid="pick-client-search"
                />
              </div>
              <div className="border border-border rounded max-h-72 overflow-y-auto">
                {(() => {
                  const q = pickClientSearch.trim().toLowerCase();
                  // Allow ANY existing client (individual OR dealer) as an
                  // end-client of this dealer. Build a combined pool from the
                  // independent rosters so the picker stays usable regardless
                  // of the table's Type / dealer-link filter state.
                  const pool = [...allIndividuals, ...allDealers];
                  const seen = new Set();
                  const dedup = pool.filter((c) => {
                    if (!c?.user_id) return false;
                    if (seen.has(c.user_id)) return false;
                    seen.add(c.user_id);
                    return true;
                  });
                  const candidates = dedup.filter((c) =>
                    c.user_id !== selectedClient?.user_id &&
                    !c.master_dealer_id
                  );
                  const filtered = q ? candidates.filter((c) => (
                    (c.name || '').toLowerCase().includes(q) ||
                    (c.phone || '').toLowerCase().includes(q) ||
                    (c.nin_number || '').toLowerCase().includes(q) ||
                    (c.passport_number || '').toLowerCase().includes(q) ||
                    (c.id_card_number || '').toLowerCase().includes(q) ||
                    (c.email || '').toLowerCase().includes(q) ||
                    (c.company_name || '').toLowerCase().includes(q)
                  )) : candidates;
                  if (filtered.length === 0) {
                    return <p className="p-4 text-sm text-muted-foreground italic text-center">No matching clients. Switch to "Create new" to add one fresh.</p>;
                  }
                  return filtered.slice(0, 100).map((c) => (
                    <label
                      key={c.user_id}
                      className={`flex items-start gap-3 p-3 border-b border-border cursor-pointer hover:bg-muted/30 ${pickClientId === c.user_id ? 'bg-primary/5' : ''}`}
                      data-testid={`pick-client-${c.user_id}`}
                    >
                      <input
                        type="radio" name="pick"
                        value={c.user_id} checked={pickClientId === c.user_id}
                        onChange={() => setPickClientId(c.user_id)}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">
                          {c.name}
                          {c.client_type === 'dealer' && (
                            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Dealer</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground space-x-3">
                          {c.phone && <span>📞 {c.phone}</span>}
                          {c.email && <span>✉️ {c.email}</span>}
                          {c.nin_number && <span>NIN {c.nin_number}</span>}
                          {c.passport_number && <span>Passport {c.passport_number}</span>}
                        </div>
                        {c.address && <div className="text-[11px] text-muted-foreground truncate">📍 {c.address}</div>}
                      </div>
                    </label>
                  ));
                })()}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setBeneficiaryDialogOpen(false)}>{t('common.cancel')}</Button>
                <Button type="submit" className="btn-primary" disabled={!pickClientId} data-testid="assign-existing-client-btn">
                  Assign to dealer
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleAddBeneficiary} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label>Full name *</Label>
                  <Input
                    value={newBeneficiary.name}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, name: e.target.value })}
                    required className="input-brutalist"
                    placeholder="Name as on ID"
                    data-testid="ben-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label>NIN (National ID)</Label>
                  <Input
                    value={newBeneficiary.nin_number}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, nin_number: e.target.value })}
                    className="input-brutalist font-mono"
                    placeholder="18 digits"
                    data-testid="ben-nin"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Passport #</Label>
                  <Input
                    value={newBeneficiary.passport_number}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, passport_number: e.target.value })}
                    className="input-brutalist font-mono"
                    data-testid="ben-passport"
                  />
                </div>
                <div className="space-y-1">
                  <Label>ID Card #</Label>
                  <Input
                    value={newBeneficiary.id_card_number}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, id_card_number: e.target.value })}
                    className="input-brutalist font-mono"
                    data-testid="ben-idcard"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    value={newBeneficiary.phone}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, phone: e.target.value })}
                    className="input-brutalist"
                    data-testid="ben-phone"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input
                    type="email" value={newBeneficiary.email}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, email: e.target.value })}
                    className="input-brutalist"
                    data-testid="ben-email"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Date of birth</Label>
                  <Input
                    type="date" value={newBeneficiary.date_of_birth}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, date_of_birth: e.target.value })}
                    className="input-brutalist"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label>Full address</Label>
                  <Input
                    value={newBeneficiary.address}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, address: e.target.value })}
                    className="input-brutalist"
                    placeholder="Street, city, wilaya"
                    data-testid="ben-address"
                  />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label>Notes</Label>
                  <Textarea
                    rows={2} value={newBeneficiary.notes}
                    onChange={(e) => setNewBeneficiary({ ...newBeneficiary, notes: e.target.value })}
                    className="input-brutalist"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setBeneficiaryDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" className="btn-primary" data-testid="add-beneficiary-submit">
                  Add End Client
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Import Clients from Excel</DialogTitle></DialogHeader>
          <div className="space-y-4" data-testid="import-dialog">
            {!importResult ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Upload an Excel file (.xlsx) with client data. Download the template first to see the correct format.
                </p>
                <div className="border-2 border-dashed border-border p-4 text-center">
                  <a href={getImportTemplateUrl()} target="_blank" rel="noopener noreferrer" className="text-sm text-primary font-medium hover:underline" data-testid="download-template">
                    Download Template (.xlsx)
                  </a>
                  <p className="text-xs text-muted-foreground mt-1">Fill in your clients, then upload below</p>
                </div>
                <div>
                  <Label className="text-sm">Upload Excel File</Label>
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    disabled={importing}
                    data-testid="import-file-input"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setImporting(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await importClientsExcel(fd);
                        setImportResult(res.data);
                        if (res.data.imported > 0) {
                          toast.success(`${res.data.imported} clients imported`);
                          loadClients();
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Import failed');
                      } finally { setImporting(false); }
                    }}
                    className="mt-1"
                  />
                  {importing && <p className="text-sm text-primary mt-2">Importing...</p>}
                </div>
              </>
            ) : (
              <div data-testid="import-result">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="border p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{importResult.imported}</p>
                    <p className="text-xs text-muted-foreground">Imported</p>
                  </div>
                  <div className="border p-3 text-center">
                    <p className="text-2xl font-bold text-red-500">{importResult.skipped}</p>
                    <p className="text-xs text-muted-foreground">Skipped</p>
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="border border-red-200 bg-red-50 p-3 max-h-40 overflow-y-auto">
                    <p className="text-xs font-semibold text-red-700 mb-1">Errors:</p>
                    {importResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-600">{err}</p>
                    ))}
                  </div>
                )}
                <Button onClick={() => { setImportResult(null); setImportDialogOpen(false); }} className="w-full mt-3">Done</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk update dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-update-dialog">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'type' ? `Change type for ${selectedIds.length} client(s)` : `Assign ${selectedIds.length} client(s) to a dealer`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            {bulkAction === 'type' ? (
              <div>
                <Label>New type</Label>
                <Select value={bulkTargetType} onValueChange={setBulkTargetType}>
                  <SelectTrigger data-testid="bulk-target-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="dealer">Dealer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Note: switching a client to <strong>dealer</strong> doesn't create portal logins automatically.
                  Use the Dealers page to add staff logins.
                </p>
              </div>
            ) : (
              <div>
                <Label>Target dealer</Label>
                <Select value={bulkTargetDealer || 'none'} onValueChange={(v) => setBulkTargetDealer(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="bulk-target-dealer"><SelectValue placeholder="Pick a dealer" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— Unassign from any dealer —</SelectItem>
                    {allDealers.map((d) => (
                      <SelectItem key={d.user_id} value={d.user_id}>
                        {d.name}{d.company_name ? ` (${d.company_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Selected clients will get the master dealer set, and a matching end-client record will be created on the dealer's account.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
            <Button onClick={submitBulkUpdate} disabled={bulkUpdating} data-testid="bulk-update-confirm">
              {bulkUpdating ? 'Applying…' : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
